import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingAlongService } from '../services/SingAlongService';
import { useSingAlongBasePath } from '../hooks/useSingAlongBasePath';
import { Music, Users, Trophy, Clock, Star, ArrowRight, Mic as Mic2, LayoutDashboard, LogOut } from 'lucide-react';

export const SingAlongLanding: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSingAlongBasePath();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [accountDest, setAccountDest] = useState(`/${basePath}/dashboard`);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (user?.email_confirmed_at) {
        setUserEmail(user.email || '');
        try {
          setAccountDest(await SingAlongService.getAccountDestination(basePath));
        } catch {
          setAccountDest(`/${basePath}/dashboard`);
        }
      }
    })();
  }, [basePath]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await SingAlongService.signOut();
      setUserEmail(null);
    } catch {
      setSigningOut(false);
    }
  };

  const steps = [
    { icon: <Star size={28} />, title: 'Sign Up', desc: 'Create your account and verify your email' },
    { icon: <Clock size={28} />, title: 'Pick a Pass', desc: 'Choose from 24-hour to 1-month options' },
    { icon: <Mic2 size={28} />, title: 'Name Your Kiosk', desc: 'Create a custom URL for your sing-along' },
    { icon: <Users size={28} />, title: 'Invite Friends', desc: 'Share the passcode and start singing' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-blue-600/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(249,115,22,0.15),transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-20 sm:py-32">
          <div className="text-center">
            {userEmail && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 max-w-xl mx-auto">
                <span className="text-sm text-white/60">
                  Signed in as <span className="font-semibold text-white">{userEmail}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(accountDest)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold transition whitespace-nowrap"
                  >
                    <LayoutDashboard size={16} />
                    My Dashboard
                  </button>
                  <button
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/15 text-white/70 hover:text-white hover:bg-white/10 text-sm font-medium transition disabled:opacity-50 whitespace-nowrap"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm font-medium mb-8">
              <Music size={16} />
              Host Your Own Sing-Along
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[1.1]">
              Turn Any Gathering Into a
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                Singing Competition
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed">
              Create your own private karaoke leaderboard. Pick songs, invite friends and family,
              and see who can hit the high notes. No app download required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate(`/${basePath}/signup`)}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Get Started
                <ArrowRight size={20} className="inline ml-2" />
              </button>
              <button
                onClick={() => {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-white/5 border border-white/15 text-white font-semibold text-lg rounded-xl hover:bg-white/10 transition"
              >
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">How It Works</h2>
            <p className="text-white/50 text-lg">Four simple steps to your own sing-along</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div
                key={i}
                className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6 text-center hover:border-orange-500/30 transition-colors"
              >
                <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-orange-500 text-white font-bold text-sm flex items-center justify-center">
                  {i + 1}
                </div>
                <div className="w-14 h-14 rounded-xl bg-orange-500/10 text-orange-400 flex items-center justify-center mx-auto mb-4">
                  {step.icon}
                </div>
                <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-white/50">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 sm:py-28 border-t border-white/5 bg-white/[0.01]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
              <Trophy size={32} className="text-amber-400 mb-4" />
              <h3 className="font-bold text-xl mb-2">Live Leaderboard</h3>
              <p className="text-white/50">Real-time scoring with AI-powered vocal analysis. See who has the best pitch and timing.</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
              <Users size={32} className="text-blue-400 mb-4" />
              <h3 className="font-bold text-xl mb-2">Unlimited Participants</h3>
              <p className="text-white/50">Share a passcode and anyone can join. No downloads, no signups -- just scan and sing.</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
              <Music size={32} className="text-orange-400 mb-4" />
              <h3 className="font-bold text-xl mb-2">Curated Song Bundles</h3>
              <p className="text-white/50">Choose from themed song collections -- pop hits, classics, holiday favorites, and more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Simple Pricing</h2>
            <p className="text-white/50 text-lg">Choose the pass that fits your event</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: '24-Hour', price: '$9.99', bundles: '1 song bundle', hours: '24 hours' },
              { name: '3-Day', price: '$19.99', bundles: '3 song bundles', hours: '3 days', tag: 'POPULAR' },
              { name: '7-Day', price: '$29.99', bundles: '7 song bundles', hours: '7 days', tag: 'BEST VALUE' },
              { name: '1-Month', price: '$49.99', bundles: 'Full catalog', hours: '30 days' },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`relative bg-white/[0.03] border rounded-2xl p-6 flex flex-col ${
                  tier.tag ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' : 'border-white/10'
                }`}
              >
                {tier.tag && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                    {tier.tag}
                  </div>
                )}
                <h3 className="font-bold text-lg mb-1">{tier.name}</h3>
                <div className="text-3xl font-black mb-4">{tier.price}</div>
                <div className="space-y-2 text-sm text-white/60 flex-1">
                  <p>{tier.bundles}</p>
                  <p>{tier.hours} access</p>
                  <p>Unlimited participants</p>
                  <p>Live leaderboard</p>
                  <p>AI scoring</p>
                </div>
                <button
                  onClick={() => navigate(`/${basePath}/signup`)}
                  className="mt-6 w-full py-3 rounded-xl font-semibold transition text-center bg-white/10 hover:bg-white/15 border border-white/15"
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4">Ready to Sing?</h2>
          <p className="text-white/50 text-lg mb-8">
            Set up takes less than 2 minutes. Your friends will be singing in no time.
          </p>
          <button
            onClick={() => navigate(`/${basePath}/signup`)}
            className="px-10 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Create Your Sing-Along
          </button>
        </div>
      </section>
    </div>
  );
};

export default SingAlongLanding;
